import React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import Search from 'lucide-react/dist/esm/icons/search';
import styled from 'styled-components';
import { cn } from '~/lib/utils';
import PropTypes from 'prop-types';

const RawInput = styled(CommandPrimitive.Input)`
	&, &:focus {
		border: none !important;
		box-shadow: none !important;
	}
`;

const RawList = styled(CommandPrimitive.List)`
	scrollbar-width: none;
	&::-webkit-scrollbar { display: none; }
`;

const StyledGroup = styled(CommandPrimitive.Group)`
	[cmdk-group-heading] {
		padding: 0.25rem 0.5rem;
		font-size: 0.6875rem;
		font-weight: 500;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: hsl(var(--muted-foreground));
	}
`;

const StyledItem = styled(CommandPrimitive.Item)`
	&[data-selected=true],
	&[aria-selected=true] {
		background-color: hsl(var(--midnight-400));
		color: hsl(var(--foreground));
	}
`;

const Command = React.forwardRef(({ className, ...props }, ref) => (
	<CommandPrimitive
		ref={ref}
		className={cn('flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground', className)}
		{...props}
	/>
));
Command.displayName = CommandPrimitive.displayName;
Command.propTypes = { className: PropTypes.string };

const CommandInput = React.forwardRef(({ className, ...props }, ref) => (
	<div className="flex items-center border-b border-border px-3" cmdk-input-wrapper="" /* eslint-disable-line react/no-unknown-property */>
		<Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
		<RawInput
			ref={ref}
			className={cn(
				'flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
				className
			)}
			{...props}
		/>
	</div>
));
CommandInput.displayName = CommandPrimitive.Input.displayName;
CommandInput.propTypes = { className: PropTypes.string };

const CommandList = React.forwardRef(({ className, ...props }, ref) => (
	<RawList
		ref={ref}
		className={cn('max-h-80 overflow-y-auto overflow-x-hidden', className)}
		{...props}
	/>
));
CommandList.displayName = CommandPrimitive.List.displayName;
CommandList.propTypes = { className: PropTypes.string };

const CommandEmpty = React.forwardRef((props, ref) => (
	<CommandPrimitive.Empty
		ref={ref}
		className="py-6 text-center text-sm text-muted-foreground"
		{...props}
	/>
));
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef(({ className, ...props }, ref) => (
	<StyledGroup
		ref={ref}
		className={cn('overflow-hidden p-1 text-foreground', className)}
		{...props}
	/>
));
CommandGroup.displayName = CommandPrimitive.Group.displayName;
CommandGroup.propTypes = { className: PropTypes.string };

const CommandSeparator = React.forwardRef(({ className, ...props }, ref) => (
	<CommandPrimitive.Separator
		ref={ref}
		className={cn('-mx-1 h-px bg-border', className)}
		{...props}
	/>
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;
CommandSeparator.propTypes = { className: PropTypes.string };

const CommandItem = React.forwardRef(({ className, ...props }, ref) => (
	<StyledItem
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			className
		)}
		{...props}
	/>
));
CommandItem.displayName = CommandPrimitive.Item.displayName;
CommandItem.propTypes = { className: PropTypes.string };

const CommandShortcut = ({ className, ...props }) => (
	<span className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)} {...props} />
);
CommandShortcut.displayName = 'CommandShortcut';
CommandShortcut.propTypes = { className: PropTypes.string };

export {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
	CommandShortcut
};
